/** Ernaehrungs-Score (F-35 D): Zahl in der Farbskala (D-04), darunter die Aufschluesselung. */
import { Link } from 'react-router-dom';

import { KRITERIEN, type TagMitScore } from '@/domain/ernaehrung/analyse';
import { skalaFarbe } from '@/domain/rating';
import { formatDe } from '@/lib/datetime';
import { SkalaStreifen } from '@/ui/composites/SkalaStreifen';
import { Haken } from '@/ui/composites/Haken';
import { cn } from '@/lib/utils/cn';
import { Karte } from '@/modules/training/auswertung/Karte';
import { zahl } from '@/modules/ernaehrung/format';

export function ScoreZahl({ wert, gross }: { wert: number | null; gross?: boolean }) {
  if (wert === null) return <span className="text-ink-faint">–</span>;
  return <span className={`inline-grid place-items-center rounded-md font-semibold tabular-nums text-canvas ${gross ? 'h-12 min-w-12 px-2 text-2xl' : 'h-6 min-w-6 px-1 text-[12px]'}`} style={{ backgroundColor: skalaFarbe(Math.round(wert)) }}>{zahl(wert)}</span>;
}

export function ScoreKarte({ tage, schnitt, einzeltag }: { tage: TagMitScore[]; schnitt: number | null; einzeltag: boolean }) {
  const t = tage[0];
  if (einzeltag && t) {
    return (
      <Karte titel="Ernährungs-Score" frage="Wie gut habe ich mich an diesem Tag ernährt – gemessen an meinen Kriterien?">
        <div className="flex items-center gap-3">
          <ScoreZahl wert={t.score.score} gross />
          <p className="text-sm text-ink-muted">{t.score.score === null ? (t.zeile.mahlzeiten ? `Nur ${t.zeile.mahlzeiten} ${t.zeile.mahlzeiten === 1 ? 'Mahlzeit' : 'Mahlzeiten'} erfasst – für einen Score den Tag vervollständigen oder als vollständig markieren.` : 'Noch nichts erfasst.') : 'von 10'}</p>
        </div>
        <ul className="flex flex-col gap-0.5 text-sm">
          {t.score.teile.map((p) => (
            <li key={p.art} className={cn('flex items-center gap-1.5', !p.erfuellt && 'text-ink-muted')}>
              <Haken ja={p.erfuellt} />
              <span>{p.text}{p.abweichung ? ` (${p.abweichung})` : ''}{p.gewicht > 1 ? <span className="text-ink-faint"> · Gewicht {p.gewicht}</span> : null}</span>
            </li>
          ))}
          {t.score.nichtPruefbar.map((a) => <li key={a} className="text-ink-faint">{KRITERIEN[a].kurz}: nicht prüfbar (kein Ziel oder keine Daten)</li>)}
        </ul>
        <Link to={`/ernaehrung?tag=${t.zeile.datum}`} className="text-[12px] text-accent-text hover:underline">Zum Tagebuch dieses Tages</Link>
      </Karte>
    );
  }
  const voll = tage.filter((x) => x.zeile.vollstaendig).length;
  return (
    <Karte titel="Ernährungs-Score" frage="Wie gut habe ich mich im Schnitt ernährt? Nur vollständige Tage zählen.">
      <div className="flex items-center gap-3">
        <ScoreZahl wert={schnitt === null ? null : Math.round(schnitt * 10) / 10} gross />
        <p className="text-sm text-ink-muted">Ø aus {voll} vollständigen von {tage.length} Tagen</p>
      </div>
      <SkalaStreifen label="Score je Tag" punkte={tage.map((x) => ({ key: x.zeile.datum, titel: formatDe(x.zeile.datum, 'EEEEEE dd.MM.'), ...(x.score.score !== null ? { wert: x.score.score } : {}) }))} />
      <ul className="flex max-h-72 flex-col gap-1 overflow-auto text-[12px]">
        {tage.filter((x) => x.score.score !== null).map((x) => (
          <li key={x.zeile.datum} className="flex gap-2">
            <Link to={`/ernaehrung?tag=${x.zeile.datum}`} className="w-16 shrink-0 hover:underline">{formatDe(x.zeile.datum, 'EEEEEE dd.MM.')}</Link>
            <ScoreZahl wert={x.score.score} />
            <span className="flex flex-wrap items-center gap-x-2 text-ink-muted">
              {x.score.teile.map((p) => (
                <span key={p.art} className="inline-flex items-center gap-0.5"><Haken ja={p.erfuellt} />{p.text}{p.abweichung ? ` (${p.abweichung})` : ''}</span>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </Karte>
  );
}
